// Loose-FK JOIN snippet filters: a loose relationship's `sourceFilter` (a
// polymorphic FK's type guard, e.g. `target_type = 'post'`) and `targetFilter`
// (e.g. `deleted_at IS NULL`) are part of what the relationship means, so the
// suggested JOIN's ON carries them — ANDed on, parenthesised, each qualified by
// its own side's alias (the new relation is the snippet's `${1}` tab stop).
// Kept in the ON rather than a WHERE so an outer join keeps its unmatched
// rows. Mirrors the VQE's resolver-side emitter (`looseRelJoinCandidates.ts`).

import type { AliasBinding, CompletionCandidate, DatabaseEngineId, TableMeta } from '../../../core/engines';
import type { LooseRelationship } from '@/types/electron';
import { escapeSnippetText, snippetWithAliasStop } from '../../sql-resolver/suggest/snippetAliasStop';
import { buildQualifiedFilterSql, filterColumnLookup } from '../../workspace/common/utils/qualifiedFilterSql';

import { boundAliasSql, generateShortAlias } from './fkJoinSnippet';

/** The part of a column a filter literal needs: its name and data type. */
export type FilterColumnType = { readonly name: string; readonly dataType?: string };

export function looseRelHasFilters(rel: LooseRelationship): boolean {
  return (rel.sourceFilter?.length ?? 0) > 0 || (rel.targetFilter?.length ?? 0) > 0;
}

/**
 * `snippet` with the relationship's filters appended to its ON. `candidateIsSource`
 * says which side holds the relationship's source (from) table — the new
 * relation, or the bound one. Unchanged when the relationship has no filters.
 */
export function withLooseFilters(
  snippet: CompletionCandidate,
  rel: LooseRelationship,
  binding: AliasBinding,
  candidate: TableMeta,
  candidateIsSource: boolean,
  existingAliases: ReadonlySet<string>,
  engine: DatabaseEngineId,
  /** Each side's columns: a filter field in its catalog spelling, a date value as the engine's date literal. */
  columns: { readonly candidate?: readonly FilterColumnType[]; readonly bound?: readonly FilterColumnType[] } = {},
): CompletionCandidate {
  if (!looseRelHasFilters(rel)) return snippet;
  const bound = boundAliasSql(binding);
  // Each side's filtered column resolved on that side (`filterColumnLookup`).
  const sourceColumn = filterColumnLookup(candidateIsSource ? columns.candidate : columns.bound);
  const targetColumn = filterColumnLookup(candidateIsSource ? columns.bound : columns.candidate);
  const tailFor = (newAlias: string): string => {
    const filters = [
      buildQualifiedFilterSql(rel.sourceFilter, candidateIsSource ? newAlias : bound, engine, sourceColumn),
      buildQualifiedFilterSql(rel.targetFilter, candidateIsSource ? bound : newAlias, engine, targetColumn),
    ].filter((sql) => sql.length > 0);
    return filters.length > 0 ? ` AND ${filters.join(' AND ')}` : '';
  };
  const labelAlias = generateShortAlias(candidate.name, existingAliases);
  const labelTail = tailFor(labelAlias);
  if (labelTail.length === 0) return snippet;
  // The new relation is the `${1}` tab stop — found by a stand-in no filter value holds.
  const insertTail = snippetWithAliasStop(tailFor, labelAlias) ?? escapeSnippetText(labelTail);
  const insertText = snippet.insertText.endsWith('$0')
    ? `${snippet.insertText.slice(0, -2)}${insertTail}$0`
    : `${snippet.insertText}${insertTail}`;
  return { ...snippet, insertText, label: `${snippet.label}${labelTail}` };
}
